import React, {useState, useRef, useEffect} from "react";
import styled from "styled-components";
import {colors} from "nxtconstants";

function youtubeTestimonialEmbedSrc (videoId) {
  return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&mute=0`;
}

function useWindowWidth () {
  const [width, setWidth] = useState(null);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

function getCardPosition (idx, activeIdx, length) {
  if (idx === activeIdx) return 0;
  if ((idx + 1) % length === activeIdx) return -1;
  if ((idx - 1 + length) % length === activeIdx) return 1;
  if ((idx + 2) % length === activeIdx) return -2;
  return null;
}

function GrowClientsWealth ({testimonials = [], partnerLogos = [], content = {}}) {
  const carouselCards =
  testimonials && testimonials.length > 0
    ? testimonials
    : content.defaultTestimonials || [];

  const logos = partnerLogos && partnerLogos.length > 0 ? partnerLogos : [];

  const [activeIdx, setActiveIdx] = useState(0);
  const trackRef = useRef(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const width = useWindowWidth();
  const isMobile = width && width <= 768;
  /** When section is off-screen, drop iframes (stops playback + avoids any embed churn). */
  const [embedsSuspended, setEmbedsSuspended] = useState(false);

  const handleNext = () => {
    setActiveIdx((activeIdx + 1) % carouselCards.length);
  };

  const handlePrev = () => {
    setActiveIdx((activeIdx - 1 + carouselCards.length) % carouselCards.length);
  };

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    if (touchStart - touchEnd > 50) {
      handleNext();
    }
    if (touchStart - touchEnd < -50) {
      handlePrev();
    }
  };

  useEffect(() => {
    const section = document.getElementById("grow-clients-wealth");
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setEmbedsSuspended(!entry.isIntersecting);
      },
      {threshold: 0.1}
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Section id="grow-clients-wealth">
      <Heading>{content.heading}</Heading>
      <Subheading>{content.subHeading}</Subheading>
      <LogoRow>
        {partnerLogos.map((logo, idx) => (
          <LogoImg
            key={idx}
            src={logo.src || logo}
            alt={logo.alt || "Partner Logo"}
            width={200}
            height={80}
            loading="lazy"
            decoding="async"
          />
        ))}
      </LogoRow>
      <SubheadingSmall>{content.subHeadingSmall}</SubheadingSmall>
      <CarouselContainer>
        <ArrowImgLeft
          src={content.arrowLeft}
          alt="Previous"
          width={96}
          height={96}
          onClick={handlePrev}
        />

        <CardsTrack
          ref={trackRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {carouselCards.map((card, idx) => {
            const pos = getCardPosition(idx, activeIdx, carouselCards.length);
            if (pos === null) return null;

            const isActiveCard = pos === 0;

            let style = {};
            if (pos === 0) {
              style = {
                left: "50%",
                transform: "translateX(-50%) scale(1.05)",
                zIndex: 2,
                opacity: 1,
                boxShadow: "0 1rem 3rem rgba(60,40,120,0.18)",
                pointerEvents: "auto"
              };
            } else if (pos === 1) {
              style = {
                left: "75%",
                transform: "translateX(-50%) scale(0.85)",
                zIndex: 1,
                opacity: 0.7,
                pointerEvents: "none"
              };
            } else if (pos === -1) {
              style = {
                left: "25%",
                transform: "translateX(-50%) scale(0.85)",
                zIndex: 1,
                opacity: 0.7,
                pointerEvents: "none"
              };
            } else if (pos === -2) {
              style = {
                display: "none"
              };
            }

            if (isMobile) {
              if (pos === 0) {
                style = {
                  left: "50%",
                  transform: "translateX(-50%) scale(1.0)",
                  zIndex: 2,
                  opacity: 1,
                  boxShadow: "0 1rem 3.5rem rgba(60,40,120,0.18)",
                  pointerEvents: "auto"
                };
              } else if (pos === 1) {
                style = {
                  left: "80%",
                  transform: "translateX(-60%) scale(0.8)",
                  zIndex: 1,
                  opacity: 0.6,
                  pointerEvents: "none"
                };
              } else if (pos === -1) {
                style = {
                  left: "20%",
                  transform: "translateX(-40%) scale(0.8)",
                  zIndex: 1,
                  opacity: 0.6,
                  pointerEvents: "none"
                };
              } else if (pos === -2) {
                style = {
                  display: "none"
                };
              }
            }

            if (width && width <= 600) {
              if (pos === 1) {
                style = {
                  ...style,
                  left: "75%",
                  transform: "translateX(-50%) scale(0.75)",
                  zIndex: 1,
                  opacity: 0.5,
                  pointerEvents: "none"
                };
              } else if (pos === -1) {
                style = {
                  ...style,
                  left: "25%",
                  transform: "translateX(-50%) scale(0.75)",
                  zIndex: 1,
                  opacity: 0.5,
                  pointerEvents: "none"
                };
              }
            }

            return (
              <CarouselCard
                key={idx}
                style={style}
              >
                <VideoWrapperTop $isActive={isActiveCard}>
                  {pos === -2 ? (
                    <div
                      aria-hidden
                      style={{width: "100%", height: "100%", background: "#000"}}
                    />
                  ) : !embedsSuspended && isActiveCard ? (
                    <VideoIframe
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="eager"
                      src={youtubeTestimonialEmbedSrc(card.videoId)}
                      title={`Video testimonial from ${card.name}`}
                    />
                  ) : (
                    <VideoPoster
                      alt={`Video thumbnail — ${card.name}`}
                      decoding="async"
                      loading={isActiveCard ? "eager" : "lazy"}
                      src={`https://i.ytimg.com/vi/${card.videoId}/hqdefault.jpg`}
                    />
                  )}
                </VideoWrapperTop>

                <TextWrapperBottom>
                  <CardText>{card.text}</CardText>
                  <CardName>{card.name}</CardName>
                  <CardCompany>{card.company}</CardCompany>
                </TextWrapperBottom>
              </CarouselCard>
            );
          })}
        </CardsTrack>

        <ArrowImgRight
          src={content.arrowRight}
          alt="Next"
          width={96}
          height={96}
          onClick={handleNext}
        />
      </CarouselContainer>
    </Section>
  );
}

const Section = styled.section`
    background: ${colors.textBlack};
    margin: 0 auto;
    width: 100%;
    min-height: 100vh; 
    color: ${colors.textBlack};
    text-align: center;
    overflow-x: hidden; 

    @media (max-width: 1024px) {
        padding: 6rem 0 12rem 0;
    }

    @media (max-width: 768px) {
        padding: 5rem 0 8rem 0;
        min-height: auto;
    }

    @media (max-width: 600px) {
        padding: 4rem 0 0;
    }
`;

const Heading = styled.h2`
	font-family: "DM Serif Display";
    color: ${colors.white};
	font-size: 5rem;
	font-weight: 400;
	margin: 2rem 26rem;

	@media (max-width: 768px) {
		font-size: 2.7rem;
		margin-bottom: 1.5rem;
		margin: 20px auto;
        width: 40rem;
	}
`;

const Subheading = styled.p`
	font-size: 2rem;
    color: ${colors.white};
	width: 92rem;
    height: 7rem;
	margin: 0 auto;
	margin-bottom: 4rem;
	line-height: 3.2rem;
	font-weight: 400;
	font-family: "Lato";

	@media (max-width: 768px) {
		font-size: 1.6rem;
		width: 80%;
		line-height: 1.6;
		margin-bottom: 5rem;
	}
`;

const LogoRow = styled.div`
	display: flex;
	justify-content: center;
	gap: 3rem;
	margin-bottom: 4rem;

	@media (max-width: 768px) {
		flex-wrap: wrap;
		gap: 1.5rem;
		margin-bottom: 1.5rem;
	}
`;

const LogoImg = styled.img`
	width: 20rem;

	@media (max-width: 768px) {
		width: 10rem;
	}
`;

const SubheadingSmall = styled.p`
	font-size: 2rem;
    color: ${colors.white};
	width: 67rem;
	margin: 0 auto;
	margin-bottom: 5rem;
	line-height: 3.2rem;
	font-weight: 400;
	font-family: "Lato";

	@media (max-width: 768px) {
		font-size: 1.6rem;
		width: 85%;
		line-height: 1.5;
		margin-bottom: 5rem;
	}
`;

const CarouselContainer = styled.div`
	position: relative;
	width: 100%;
	max-width: 125rem;
	margin: 0 auto;
	padding: 5rem 0;
	display: flex;
	align-items: center;
	justify-content: center;

	@media (max-width: 768px) {
		padding: 2.8rem;
	}
`;

const CardsTrack = styled.div`
    position: relative;
    width: min(85%, 100rem); 
    height: min(55rem, 100vh); 
    min-height: 65rem; 
    overflow: hidden;
    touch-action: pan-y;
    cursor: grab;

    &:active {
        cursor: grabbing;
    }

    @media (max-width: 1024px) {
        width: min(90%, 80rem);
        height: min(50rem, 70vh);
        min-height: 40rem;
    }

    @media (max-width: 768px) {
        height: min(50rem, 65vh);
        width: 95%;
        min-height: 38rem;
        overflow: hidden;
        touch-action: pan-y;
    }

    @media (max-width: 600px) {
        height: min(45rem, 60vh);
        width: 98%;
        min-height: 35rem;
    }
`;

const CarouselCard = styled.div`
    position: absolute;
    top: 0;
    width: min(44rem, 80vw);
    height: min(60rem, 90vh); 
    min-height: 50rem; 
    background: ${colors.white};
    border-radius: 3rem;
    padding: 0 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    transition: transform 0.5s cubic-bezier(0.77, 0, 0.18, 1),
        box-shadow 0.3s,
        opacity 0.3s,
        left 0.5s;
    overflow: hidden;

    @media (max-width: 1200px) {
        width: min(38rem, 75vw);
        height: min(52rem, 80vh);
        min-height: 45rem;
    }

    @media (max-width: 1024px) {
        width: min(35rem, 70vw);
        height: min(48rem, 75vh);
        min-height: 42rem;
    }

    @media (max-width: 768px) {
        width: min(31.2rem, 85vw);
        height: min(46.7rem, 70vh);
        min-height: 40rem;
        padding: 0 1rem 2rem 1rem;
    }

    @media (max-width: 600px) {
        width: min(27.8rem, 90vw);
        height: min(42.3rem, 65vh);
        min-height: 35rem;
        padding: 0 0.8rem 1.5rem 0.8rem;
    }

    @media (max-width: 480px) {
        width: min(25rem, 95vw);
        height: min(38rem, 60vh);
        min-height: 32rem;
    }
`;

const VideoIframe = styled.iframe`
    border: 0;
    display: block;
    font-size: 0.5rem;
    width: 100%;
    height: 100%;
`;

const VideoPoster = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const VideoWrapperTop = styled.div`
    position: absolute;
    background: black;
    top: 0;
    width: 100%;
    height: 55%; 
    border-top-left-radius: 2.5rem;
    border-top-right-radius: 2.5rem;
    overflow: hidden;
    pointer-events: ${props => props.$isActive ? "auto" : "none"};
    cursor: ${props => props.$isActive ? "pointer" : "default"};

    @media (max-width: 768px) {
        height: 50%; 
    }

    @media (max-width: 600px) {
        height: 45%; 
    }
`;

const CardText = styled.p`
    font-size: clamp(1.1rem, 2.2vw, 1.5rem); 
    width: 100%;
    margin-bottom: 2rem; 
    text-overflow: ellipsis;
    line-height: 1.5;
    font-weight: 400;
    color: #333; 
    
    @media (max-width: 768px) {
        font-size: 12px;
        font-weight: 400;
        font-family: "Lato";
        margin: 1rem auto 1.5rem auto;
        line-height: 1.4;
    }

    @media (max-width: 600px) {
        font-size: 10px;
        margin: 0.5rem auto 1rem auto;
        line-height: 1.3;
    }
`;

const CardName = styled.div`
    font-size: clamp(1rem, 1.8vw, 1.4rem); 
    margin-bottom: 0.5rem; 
    color: #1C1C1C;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 12px;
        margin-bottom: 0.5rem;
        font-weight: 700;
        font-family: "Lato";
    }

    @media (max-width: 600px) {
        font-size: 10px;
        margin-bottom: 0.3rem;
    }
`;

const CardCompany = styled.div`
    font-size: clamp(1rem, 1.8vw, 1.4rem); 
    margin-bottom: 0.5rem; 
    color: #1C1C1C;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 12px;
        margin-bottom: 0.5rem;
        font-weight: 700;
        font-family: "Lato";
    }

    @media (max-width: 600px) {
        font-size: 10px;
        margin-bottom: 0.3rem;
    }
`;

const ArrowImgLeft = styled.img`
	position: absolute;
	width: 6rem;
	height: 6rem;
	cursor: pointer;
	filter: brightness(0) invert(1);
	transition: filter 0.2s;
	z-index: 10;
	left: 5%;
	top: 50%;
	transform: translateY(-50%);
	
	&:hover {
		filter: brightness(0.8) invert(0.8);
	}

	@media (max-width: 768px) {
		width: 4rem;
		height: 4rem;
		left: 1rem;
		top: 50%;
		transform: translateY(-50%);
		background: rgba(255, 255, 255, 0.2);
		border-radius: 50%;
		padding: 0.8rem;
	}
`;

const ArrowImgRight = styled.img`
	position: absolute;
	width: 6rem;
	height: 6rem;
	cursor: pointer;
	filter: brightness(0) invert(1);
	transition: filter 0.2s;
	z-index: 10;
	right: 5%;
	top: 50%;
	transform: translateY(-50%);
	
	&:hover {
		filter: brightness(0.8) invert(0.8);
	}

	@media (max-width: 768px) {
		width: 4rem;
		height: 4rem;
		right: 1rem;
		top: 50%;
		transform: translateY(-50%);
		background: rgba(255, 255, 255, 0.2);
		border-radius: 50%;
		padding: 0.8rem;
	}
`;

const TextWrapperBottom = styled.div`
    width: 100%;
    height: 45%; 
    display: flex;
    flex-direction: column;
    justify-content: center; 
    align-items: center; 
    box-sizing: border-box;
    background: ${colors.white};
    padding: 1rem 0; 
    margin-top: auto;

    @media (max-width: 768px) {
        height: 50%; 
        padding: 1.5rem 0;
    }

    @media (max-width: 600px) {
        height: 55%;
        padding: 1rem 0;
    }
`;
export default GrowClientsWealth;